import { useState } from 'react'
import type { Campaign } from '@/domain/campaign.types'
import { CAMPAIGN_MESSAGE_ASSOCIATION_BATCH_LIMIT } from '@/domain/campaign.types'
import type { Message, MessageListParams } from '@/domain/message.types'
import { useCampaignMessages } from '@/hooks/useCampaignMessages'
import { useAssociateCampaignMessages } from '@/hooks/useCampaigns'
import { useMessages } from '@/hooks/useMessages'
import { useMessageResolutionMatrix } from '@/pages/Messages/useMessageResolutionMatrix'

// The Campaign Messages-section container: the resolution matrix over the copy
// associated with this Campaign, plus the picker that links existing copy to it.
// The grid itself is the Messages feature's own — this hands it the Campaign's
// rows, the Campaign's classification as a new row's seed, and the association
// call that links a row added here back to the Campaign.

/**
 * The grid lists what the Campaign is associated with, not a filtered slice of
 * the global message list, so it has no filters of its own to narrow by. A new
 * row's type and status therefore come from the draft's own dropdowns.
 */
const NO_FILTERS: MessageListParams = {}

/** A stable empty list, so an unresolved read does not hand the grid a new array on every render. */
const NO_MESSAGES: readonly Message[] = []

/**
 * Excludes messages already associated with the Campaign — the same shape
 * `visiblePickerCandidates` uses for Files — AND non-source messages: only a
 * translation group's source can be associated (its target-locale variants are
 * reached by translating from it afterward, not picked independently).
 */
function visibleMessageCandidates(candidates: readonly Message[], associatedIds: ReadonlySet<string>) {
	return candidates.filter((m) => m.isSource && !associatedIds.has(m.messageId))
}

/**
 * Builds the message-picker's confirm/retry handler — mirrors `makeSubmitAssociation`
 * in `useCampaignFilesSectionContainer.ts`. Extracted to keep the owning hook
 * under the 50-line function limit.
 */
function makeSubmitMessageAssociation(
	associationMutation: ReturnType<typeof useAssociateCampaignMessages>,
	selectedIds: ReadonlySet<string>,
	isDisabled: boolean,
	resetPickerState: () => void,
) {
	return () => {
		if (isDisabled) return
		associationMutation.mutate([...selectedIds], {
			onSuccess: () => {
				resetPickerState()
				associationMutation.reset()
			},
		})
	}
}

/**
 * Message-picker dialog state. Candidate source is `useMessages({ search })` —
 * a server-side search param, NOT a fetch-all-then-filter-client-side shape —
 * so exclusion of already-associated messages is the only client-side filtering
 * step, unlike the Files picker's full client-side search.
 */
function useCampaignMessagePicker(campaignId: string, campaignCode: string, associatedIds: ReadonlySet<string>) {
	const [isPickerOpen, setIsPickerOpen] = useState(false)
	const [pickerSearch, setPickerSearch] = useState('')
	const [pickerSelectedIds, setPickerSelectedIds] = useState<ReadonlySet<string>>(new Set())

	const candidateSearch = useMessages({ search: pickerSearch })
	const associationMutation = useAssociateCampaignMessages(campaignId, campaignCode)

	function togglePickerSelection(messageId: string) {
		setPickerSelectedIds((current) => {
			const next = new Set(current)
			if (next.has(messageId)) next.delete(messageId)
			else next.add(messageId)
			return next
		})
	}

	const isSelectionOverLimit = pickerSelectedIds.size > CAMPAIGN_MESSAGE_ASSOCIATION_BATCH_LIMIT
	const isConfirmDisabled = pickerSelectedIds.size === 0 || isSelectionOverLimit
	const submitAssociation = makeSubmitMessageAssociation(
		associationMutation,
		pickerSelectedIds,
		isConfirmDisabled,
		() => {
			setIsPickerOpen(false)
			setPickerSearch('')
			setPickerSelectedIds(new Set())
		},
	)

	return {
		isPickerOpen,
		onOpenPicker: () => {
			setPickerSelectedIds(new Set())
			setIsPickerOpen(true)
		},
		onClosePicker: () => setIsPickerOpen(false),
		pickerCandidates: visibleMessageCandidates(candidateSearch.data ?? [], associatedIds),
		isPickerLoading: candidateSearch.isLoading,
		isPickerError: candidateSearch.isError,
		pickerSearch,
		onPickerSearchChange: setPickerSearch,
		pickerSelectedIds,
		togglePickerSelection,
		isPickerSelectionOverLimit: isSelectionOverLimit,
		isConfirmDisabled,
		onConfirmPicker: submitAssociation,
		onRetryPickerAssociation: submitAssociation,
		isAssociationPending: associationMutation.isPending,
		isAssociationError: associationMutation.isError,
		isAssociationSuccess: associationMutation.isSuccess,
	}
}

/**
 * The Campaign's own resolution matrix and its link-existing picker.
 *
 * A row added from the grid is created, classified with the Campaign's own axis
 * values, and then associated with the Campaign — three writes, because a Message
 * is not a Campaign's child but a shared object the Campaign is tagged against.
 * The association is what makes the new row appear in this grid at all, since the
 * grid reads by `campaign_code`, so a create whose association failed is reported
 * as unfinished rather than closed as done.
 *
 * `campaign` is optional because the page composes its hooks before the detail
 * read resolves (Rules of Hooks); until it does, there is no Campaign code to
 * read by and the grid stays empty.
 */
export function useCampaignMessageMatrixContainer(campaignId: string, campaign: Campaign | undefined) {
	const campaignCode = campaign?.campaignCode ?? ''
	const associated = useCampaignMessages(campaignCode)
	const attachMutation = useAssociateCampaignMessages(campaignId, campaignCode)

	const matrix = useMessageResolutionMatrix(NO_FILTERS, {
		messages: associated.data ?? NO_MESSAGES,
		isLoading: associated.isLoading,
		isError: associated.isError,
		seedClassification: campaign?.classification.selection,
		attach: {
			run: async (messageId: string) => {
				await attachMutation.mutateAsync([messageId])
			},
			failureMessage: 'The message was added, but linking it to this Campaign did not save — retry to finish it.',
		},
	})

	const picker = useCampaignMessagePicker(
		campaignId,
		campaignCode,
		new Set((associated.data ?? NO_MESSAGES).map((m) => m.messageId)),
	)

	return { matrix, picker }
}
